/**
 * Client workout history. Ported from the original app's workouts/history/page.tsx —
 * same UI verbatim, with useRouter().back() → useNavigate(-1) and the api client.
 * Reads GET /api/records and GET /api/workouts/logs?limit=20.
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { ArrowLeft, Trophy, Dumbbell, Timer, ArrowUp } from 'lucide-react';
import { api } from '@/lib/api';

interface PR {
  id: string;
  weight: number;
  reps: number;
  date: string;
  previous_best_weight: number | null;
  exercises: { name: string };
}

interface WorkoutLog {
  id: string;
  date: string;
  duration_minutes: number;
  workout_type: string;
  notes: string | null;
}

export default function WorkoutHistory() {
  const navigate = useNavigate();
  const [records, setRecords] = useState<PR[]>([]);
  const [logs, setLogs] = useState<WorkoutLog[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      try {
        const [prRes, logRes] = await Promise.all([
          api.get<{ records?: PR[] }>('/api/records'),
          api.get<{ logs?: WorkoutLog[] }>('/api/workouts/logs?limit=20'),
        ]);
        setRecords(prRes.records || []);
        setLogs(logRes.logs || []);
      } catch { /* */ }
      finally { setLoading(false); }
    }
    load();
  }, []);

  return (
    <div className="px-5 pt-[max(16px,env(safe-area-inset-top))] pb-4">
      <div className="flex items-center gap-3 mb-5">
        <button onClick={() => navigate(-1)} className="p-1" style={{ color: 'var(--gold)' }}>
          <ArrowLeft size={22} />
        </button>
        <h1 className="display" style={{ fontSize: "1.9rem" }}>History</h1>
      </div>

      {loading ? (
        <div className="flex flex-col gap-3">
          {[1, 2, 3].map((i) => <div key={i} className="card animate-pulse h-16" style={{ background: 'var(--surface)' }} />)}
        </div>
      ) : (
        <div className="flex flex-col gap-5">
          {/* Personal Records */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Trophy size={16} style={{ color: 'var(--gold)' }} />
              <span className="text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--gold-muted)' }}>Personal Records</span>
            </div>
            {records.length === 0 ? (
              <div className="card text-center py-6">
                <Trophy size={32} style={{ color: 'var(--gray-600)' }} className="mx-auto mb-2" />
                <p className="text-sm font-semibold text-white">No Records Yet</p>
                <p className="text-xs mt-1" style={{ color: 'var(--gray-400)' }}>Complete workouts to set PRs</p>
              </div>
            ) : (
              records.slice(0, 10).map((pr) => (
                <div key={pr.id} className="card mb-2 flex items-center justify-between">
                  <div>
                    <p className="text-sm font-semibold text-white">{pr.exercises?.name}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-xs" style={{ color: 'var(--gray-400)' }}>{pr.weight} lbs &times; {pr.reps} reps</span>
                      <span className="text-xs" style={{ color: 'var(--gray-500)' }}>{formatDate(pr.date)}</span>
                    </div>
                  </div>
                  {pr.previous_best_weight && pr.weight > pr.previous_best_weight && (
                    <span className="text-xs px-2 py-0.5 rounded-full flex items-center gap-0.5" style={{ background: 'rgba(34,197,94,0.12)', color: 'var(--success)' }}>
                      <ArrowUp size={10} /> +{pr.weight - pr.previous_best_weight}
                    </span>
                  )}
                </div>
              ))
            )}
          </div>

          {/* Recent Workouts */}
          {logs.length > 0 && (
            <div>
              <p className="text-xs font-medium uppercase tracking-wider mb-3" style={{ color: 'var(--gold-muted)' }}>Recent Workouts</p>
              {logs.map((log) => (
                <div key={log.id} className="card mb-2 flex items-center gap-3">
                  <Dumbbell size={18} style={{ color: 'var(--gold)' }} />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-white">{log.notes || log.workout_type || 'Workout'}</p>
                    <p className="text-xs" style={{ color: 'var(--gray-400)' }}>{formatDate(log.date)}</p>
                  </div>
                  <span className="flex items-center gap-1 text-xs" style={{ color: 'var(--gray-400)' }}>
                    <Timer size={12} /> {log.duration_minutes} min
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function formatDate(date: string): string {
  return new Date(date + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}
